// App — top-level route tree.
//
// Public surfaces: marketing landing, /docs (two-column chrome), auth flows.
// Authenticated surfaces: AuthGate → TeamProvider → DashboardShell.

import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom'
import { AuthGate } from './components/AuthGate.jsx'
import { DashboardShell } from './components/dashboard/DashboardShell.jsx'
import { TeamProvider } from './context/TeamProvider.jsx'
import { NotFound } from './routes/NotFound.jsx'
import { AdminHealth } from './routes/app/AdminHealth.jsx'
import { AiModels } from './routes/app/AiModels.jsx'
import { Billing } from './routes/app/Billing.jsx'
import { DriftReport } from './routes/app/DriftReport.jsx'
import { FlowDetail } from './routes/app/FlowDetail.jsx'
import { FlowsList } from './routes/app/FlowsList.jsx'
import { Members } from './routes/app/Members.jsx'
import { ProjectHome } from './routes/app/ProjectHome.jsx'
import { ProjectSettings } from './routes/app/ProjectSettings.jsx'
import { RunDetail } from './routes/app/RunDetail.jsx'
import { RunsList } from './routes/app/RunsList.jsx'
import { TeamHome } from './routes/app/TeamHome.jsx'
import { Teams } from './routes/app/Teams.jsx'
import { UserSettings } from './routes/app/UserSettings.jsx'
import { CliCallback } from './routes/auth/CliCallback.jsx'
import { ForgotPassword } from './routes/auth/ForgotPassword.jsx'
import { Login } from './routes/auth/Login.jsx'
import { Signup } from './routes/auth/Signup.jsx'
import { DocsIndex } from './routes/docs/DocsIndex.jsx'
import { DocsLayout } from './routes/docs/DocsLayout.jsx'
import { DocsPage } from './routes/docs/DocsPage.jsx'
import { Landing } from './routes/marketing/Landing.jsx'

export function App() {
  return (
    <BrowserRouter>
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <Routes>
        {/* Public — no auth required */}
        <Route path="/" element={<Landing />} />
        <Route path="/auth/login" element={<Login />} />
        <Route path="/auth/signup" element={<Signup />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/cli" element={<CliCallback />} />

        {/* Docs — public, two-column chrome with sidebar */}
        <Route path="/docs" element={<DocsLayout />}>
          <Route index element={<DocsIndex />} />
          <Route path=":slug" element={<DocsPage />} />
        </Route>

        {/* Authenticated — wrapped in AuthGate + TeamProvider + DashboardShell */}
        <Route
          element={
            <AuthGate>
              <TeamProvider>
                <Outlet />
              </TeamProvider>
            </AuthGate>
          }
        >
          <Route element={<DashboardShell />}>
            <Route path="/teams" element={<Teams />} />
            <Route path="/teams/:teamId" element={<TeamHome />} />
            <Route path="/teams/:teamId/members" element={<Members />} />
            <Route path="/teams/:teamId/ai-models" element={<AiModels />} />
            <Route path="/teams/:teamId/billing" element={<Billing />} />
            <Route path="/teams/:teamId/projects/:projectId" element={<ProjectHome />} />
            <Route path="/teams/:teamId/projects/:projectId/flows" element={<FlowsList />} />
            <Route
              path="/teams/:teamId/projects/:projectId/flows/:flowId"
              element={<FlowDetail />}
            />
            <Route path="/teams/:teamId/projects/:projectId/runs" element={<RunsList />} />
            <Route path="/teams/:teamId/projects/:projectId/runs/:runId" element={<RunDetail />} />
            <Route path="/teams/:teamId/projects/:projectId/drift" element={<DriftReport />} />
            <Route
              path="/teams/:teamId/projects/:projectId/settings"
              element={<ProjectSettings />}
            />
            <Route path="/user/settings" element={<UserSettings />} />
            <Route path="/admin/health" element={<AdminHealth />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  )
}
